// Landing "Upgrade to Pro" checkout intent, persisted across a fresh Clerk
// signup. Clerk drops forceRedirectUrl on fresh signups (→ "/" → dashboard →
// welcome), so the dashboard funnel consumes this to honor straight-to-checkout
// rather than dropping the user into onboarding and losing the intent.

export const CHECKOUT_INTENT_KEY = "paperchase-checkout-intent";

export type CheckoutIntent = { checkout: "monthly" | "annual"; promo?: string };

/**
 * Stash a checkout intent derived from a signUpRedirectUrl like
 * "/settings/billing?checkout=annual&promo=X". No-op unless the URL carries a
 * valid checkout period, so only the Pro CTA (which sets that redirect) stashes.
 */
export function stashCheckoutIntent(redirectUrl?: string): void {
  if (!redirectUrl || typeof window === "undefined") return;
  const qs = redirectUrl.split("?")[1];
  if (!qs) return;
  const params = new URLSearchParams(qs);
  const period = params.get("checkout");
  if (period !== "monthly" && period !== "annual") return;
  try {
    const intent: CheckoutIntent = { checkout: period };
    const promo = params.get("promo");
    if (promo) intent.promo = promo;
    window.sessionStorage.setItem(CHECKOUT_INTENT_KEY, JSON.stringify(intent));
  } catch {
    /* sessionStorage unavailable (e.g. private mode) — forceRedirectUrl is the fallback */
  }
}

/** Read and clear the stashed intent. Returns null when absent or malformed. */
export function consumeCheckoutIntent(): CheckoutIntent | null {
  if (typeof window === "undefined") return null;
  const raw = window.sessionStorage.getItem(CHECKOUT_INTENT_KEY);
  if (!raw) return null;
  window.sessionStorage.removeItem(CHECKOUT_INTENT_KEY);
  try {
    const p = JSON.parse(raw);
    const checkout: "monthly" | "annual" = p?.checkout === "monthly" ? "monthly" : "annual";
    const promo = typeof p?.promo === "string" && p.promo ? p.promo : undefined;
    return { checkout, promo };
  } catch {
    return null;
  }
}
